import { useState, useRef, useEffect } from "react";
import { useProductSearch } from "../hooks/useProductSearch";
import type { ProductSearchResult } from "../../services/portalProducts";
import styles from "./ProductAutocomplete.module.css";

interface ProductAutocompleteProps {
  /** Called when a product is selected from the catalog dropdown. */
  onSelectProduct: (product: {
    sage_item_code: string;
    display_name: string;
    item_code_desc: string | null;
  }) => void;
  /** Called when free text is entered via Enter key. */
  onAddFreeText: (text: string) => void;
  placeholder?: string;
}

/**
 * Product autocomplete input with catalog search dropdown.
 *
 * - Typing triggers a debounced search against the product catalog.
 * - Selecting from the dropdown calls onSelectProduct (resolved).
 * - Pressing Enter without selecting calls onAddFreeText (unresolved).
 */
export function ProductAutocomplete({
  onSelectProduct,
  onAddFreeText,
  placeholder = "Add product...",
}: ProductAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const { results, loading, search, clear } = useProductSearch();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleInputChange(value: string) {
    setInputValue(value);
    if (value.trim().length >= 2) {
      search(value);
      setShowDropdown(true);
    } else {
      clear();
      setShowDropdown(false);
    }
  }

  function handleSelect(product: ProductSearchResult) {
    onSelectProduct({
      sage_item_code: product.sage_item_code,
      display_name: product.item_code_desc || product.sage_item_code,
      item_code_desc: product.udf_fulldesc || product.item_code_desc,
    });
    setInputValue("");
    clear();
    setShowDropdown(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        onAddFreeText(trimmed);
        setInputValue("");
        clear();
        setShowDropdown(false);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <input
        type="text"
        className={styles.input}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (results.length > 0) setShowDropdown(true);
        }}
        placeholder={placeholder}
      />
      {showDropdown && (loading || results.length > 0) && (
        <div className={styles.dropdown}>
          {loading && results.length === 0 && (
            <div className={styles.loading}>Searching...</div>
          )}
          {results.map((product) => (
            <button
              key={product.id}
              type="button"
              className={styles.dropdownItem}
              onClick={() => handleSelect(product)}
            >
              <span className={styles.itemCode}>
                {product.sage_item_code}
              </span>
              <span className={styles.itemDesc}>
                {product.item_code_desc || product.udf_fulldesc || ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
